import { useState } from "react";
import type { CatalogRangeFilter, CatalogSelectFilter } from "@apex/shared";
import { cn } from "@apex/shared";
import { Button } from "./ui/Button";
import { useWidget } from "../state";
import { FilterChip } from "./filters/FilterChip";
import { RangeFilterPopover } from "./filters/RangeFilterPopover";
import { SelectFilterPopover } from "./filters/SelectFilterPopover";

export interface FilterBarProps {
  className?: string;
}

/**
 * FilterBar — chip-row layout that mixes select and range filters into a
 * single horizontal strip above the Models grid. Each chip opens a popover:
 *
 * - SelectFilter → checkbox list popover (`SelectFilterPopover`)
 * - RangeFilter  → dual-handle slider + min/max inputs (`RangeFilterPopover`)
 *
 * Active chips show the current value (e.g. "Working Height: 10–14m") and
 * carry an inline dismiss button. A "Clear all" button appears at the end
 * of the row when at least one filter is active.
 *
 * On mobile the chip row becomes horizontally scrollable; on desktop it
 * wraps to multiple lines.
 */
export function FilterBar({ className }: FilterBarProps) {
  const {
    state,
    setFilter,
    setRangeFilter,
    clearRangeFilter,
    clearFilters,
  } = useWidget();
  const { filters, activeFilters, activeRangeFilters } = state;

  // Track which filter is currently open (by id). null = none.
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);

  if (!filters) {
    return null;
  }

  const hasSelectFilters = filters.selectFilters.length > 0;
  const hasRangeFilters = filters.rangeFilters.length > 0;
  if (!hasSelectFilters && !hasRangeFilters) {
    return null;
  }

  const hasActiveFilters =
    Object.values(activeFilters).some((v) => v) ||
    Object.keys(activeRangeFilters).length > 0;

  function chipLabelForRange(filter: CatalogRangeFilter): string {
    const range = activeRangeFilters[filter.key];
    if (!range) return filter.displayName;
    const unit = filter.unit ?? "";
    return `${filter.displayName}: ${range.min}–${range.max}${unit}`;
  }

  function chipLabelForSelect(filter: CatalogSelectFilter): string {
    const v = activeFilters[filter.key];
    if (!v) return filter.displayName;
    const option = filter.options.find((o) => o.value === v);
    return `${filter.displayName}: ${option?.displayName ?? v}`;
  }

  return (
    <div
      className={cn(
        "apex-flex apex-flex-wrap apex-items-center apex-gap-2 apex-px-6 apex-py-3 apex-border-b apex-border-border apex-bg-muted/30",
        // Mobile: horizontal scroll, no wrap
        "max-md:apex-flex-nowrap max-md:apex-overflow-x-auto",
        className
      )}
    >
      {filters.selectFilters.map((filter) => {
        const active = !!activeFilters[filter.key];
        const open = openFilterId === filter.id;
        return (
          <div key={filter.id} className="apex-relative">
            <FilterChip
              active={active}
              onClick={() => setOpenFilterId(open ? null : filter.id)}
              onDismiss={active ? () => setFilter(filter.key, "") : undefined}
            >
              {chipLabelForSelect(filter)}
            </FilterChip>
            {open && (
              <SelectFilterPopover
                filter={filter}
                value={activeFilters[filter.key] ?? ""}
                onSelect={(value) => setFilter(filter.key, value)}
                onClose={() => setOpenFilterId(null)}
              />
            )}
          </div>
        );
      })}

      {filters.rangeFilters.map((filter) => {
        const range = activeRangeFilters[filter.key] ?? null;
        const active = !!range;
        const open = openFilterId === filter.id;
        return (
          <div key={filter.id} className="apex-relative">
            <FilterChip
              active={active}
              onClick={() => setOpenFilterId(open ? null : filter.id)}
              onDismiss={active ? () => clearRangeFilter(filter.key) : undefined}
            >
              {chipLabelForRange(filter)}
            </FilterChip>
            {open && (
              <RangeFilterPopover
                filter={filter}
                value={range}
                onApply={(next) => setRangeFilter(filter.key, next)}
                onClear={() => clearRangeFilter(filter.key)}
                onClose={() => setOpenFilterId(null)}
              />
            )}
          </div>
        );
      })}

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className="apex-text-muted-foreground apex-shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="apex-mr-1"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
          Clear all
        </Button>
      )}
    </div>
  );
}
